import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";

const coutVide = { libelle: "", montant: "", type: "par_personne", categorie: "divers", type_chambre: "" };

const TYPES_CHAMBRE = [
  { value: "double", label: "Double" },
  { value: "triple", label: "Triple" },
  { value: "quadruple", label: "Quadruple" },
];

const CATEGORIES = [
  { value: "hotel", label: "Hôtel" },
  { value: "transport", label: "Transport" },
  { value: "visa", label: "Visa" },
  { value: "restauration", label: "Restauration" },
  { value: "encadrement", label: "Encadrement" },
  { value: "divers", label: "Divers" },
];

function OumrahSimulateur() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [couts, setCouts] = useState([]);
  const [nouveauCout, setNouveauCout] = useState({ ...coutVide });

  const [nbParTypeChambre, setNbParTypeChambre] = useState({ double: 10, triple: 0, quadruple: 0 });
  const [modeBenef, setModeBenef] = useState("marge_pct");
  const [valeurBenef, setValeurBenef] = useState(20);

  const fmt = (n) => parseFloat(n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 });

  const fetchSessions = async () => {
    try {
      const res = await api.get("/oumrah/sessions");
      setSessions(res.data);
    } catch (err) { console.error(err); }
  };
  const fetchCouts = async (sid) => {
    if (!sid) { setCouts([]); return; }
    try {
      const res = await api.get("/oumrah/couts/session/" + sid);
      setCouts(res.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchSessions(); }, []);
  useEffect(() => { fetchCouts(sessionId); }, [sessionId]);

  const ajouterCout = async () => {
    if (!nouveauCout.libelle) return;
    try {
      await api.post("/oumrah/couts/session/" + sessionId, {
        libelle: nouveauCout.libelle,
        montant: parseFloat(nouveauCout.montant) || 0,
        type: nouveauCout.type,
        categorie: nouveauCout.categorie,
        type_chambre: nouveauCout.categorie === "hotel" ? (nouveauCout.type_chambre || null) : null,
      });
      setNouveauCout({ ...coutVide });
      fetchCouts(sessionId);
    } catch (err) { alert(err.response?.data?.message || "Erreur"); }
  };
  const supprimerCout = async (id) => {
    try {
      await api.delete("/oumrah/couts/" + id);
      fetchCouts(sessionId);
    } catch (err) { alert(err.response?.data?.message || "Erreur"); }
  };

  const changerCategorie = (cat) => {
    setNouveauCout({ ...nouveauCout, categorie: cat, type_chambre: cat === "hotel" ? nouveauCout.type_chambre : "" });
  };

  const labelCategorie = (cat) => {
    const c = CATEGORIES.find((x) => x.value === cat);
    return c ? c.label : "divers";
  };

  const N_total = Math.max(
    0,
    (parseInt(nbParTypeChambre.double) || 0) +
    (parseInt(nbParTypeChambre.triple) || 0) +
    (parseInt(nbParTypeChambre.quadruple) || 0)
  );
  const N = Math.max(1, N_total);

  const coutsGeneriques = couts.filter((c) => !(c.categorie === "hotel" && c.type_chambre));
  const totalGeneriqueParPersonne = coutsGeneriques
    .filter((c) => c.type === "par_personne")
    .reduce((s, c) => s + parseFloat(c.montant), 0);
  const totalFixe = coutsGeneriques
    .filter((c) => c.type === "fixe_groupe")
    .reduce((s, c) => s + parseFloat(c.montant), 0);

  const v = parseFloat(valeurBenef) || 0;

  const calculerPourType = (typeChambre) => {
    const n = Math.max(0, parseInt(nbParTypeChambre[typeChambre]) || 0);
    const coutsSpecifiques = couts.filter((c) => c.categorie === "hotel" && c.type_chambre === typeChambre);
    const totalSpecifiqueParPersonne = coutsSpecifiques
      .filter((c) => c.type === "par_personne")
      .reduce((s, c) => s + parseFloat(c.montant), 0);
    const totalSpecifiqueFixe = coutsSpecifiques
      .filter((c) => c.type === "fixe_groupe")
      .reduce((s, c) => s + parseFloat(c.montant), 0);

    const partFixeGenerique = totalFixe / N;
    const partFixeSpecifique = n > 0 ? totalSpecifiqueFixe / n : 0;

    const revientPers = totalGeneriqueParPersonne + totalSpecifiqueParPersonne + partFixeGenerique + partFixeSpecifique;

    let prixVente = revientPers;
    if (modeBenef === "marge_pct") prixVente = revientPers * (1 + v / 100);
    else if (modeBenef === "benef_pers") prixVente = revientPers + v;
    else if (modeBenef === "benef_total") prixVente = revientPers + (n > 0 ? v / n : 0);

    const benefPers = prixVente - revientPers;
    const benefTotal = benefPers * n;
    const coutTotal = (totalGeneriqueParPersonne + totalSpecifiqueParPersonne) * n + partFixeSpecifique * n;
    const recetteTotal = prixVente * n;

    return { n, revientPers, prixVente, benefPers, benefTotal, coutTotal, recetteTotal, coutsSpecifiques };
  };

  const resultats = {
    double: calculerPourType("double"),
    triple: calculerPourType("triple"),
    quadruple: calculerPourType("quadruple"),
  };

  const coutTotalSession = Object.values(resultats).reduce((s, r) => s + r.coutTotal, 0) + totalFixe;
  const recetteTotalSession = Object.values(resultats).reduce((s, r) => s + r.recetteTotal, 0);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <Card>
              <MDBox mx={2} mt={-3} py={2} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
                <MDTypography variant="h6" color="white">Simulateur de prix Oumrah</MDTypography>
              </MDBox>
              <MDBox p={3}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="sess-sim">Session</InputLabel>
                  <Select labelId="sess-sim" label="Session" sx={{ height: 44 }}
                    value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
                    {sessions.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.libelle}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {sessionId && (
                  <>
                    <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
                      Coûts de la session
                    </MDTypography>
                    {couts.map((c) => (
                      <MDBox key={c.id} display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
                        <MDTypography variant="button" color="text">
                          {c.libelle} — {fmt(c.montant)} €
                          <span style={{ color: c.type === "fixe_groupe" ? "#fb8c00" : "#1A73E8", marginLeft: 6 }}>
                            ({c.type === "fixe_groupe" ? "fixe groupe" : "par pers."})
                          </span>
                          <span style={{ color: "#999", marginLeft: 6 }}>
                            — {labelCategorie(c.categorie)}
                          </span>
                          {c.categorie === "hotel" && c.type_chambre && (
                            <span style={{ color: "#9c27b0", marginLeft: 6, fontStyle: "italic" }}>
                              ({TYPES_CHAMBRE.find((t) => t.value === c.type_chambre)?.label || c.type_chambre})
                            </span>
                          )}
                        </MDTypography>
                        <IconButton size="small" onClick={() => supprimerCout(c.id)}>
                          <Icon fontSize="small">delete</Icon>
                        </IconButton>
                      </MDBox>
                    ))}
                    {couts.length === 0 && (
                      <MDTypography variant="caption" color="text" display="block">Aucun coût saisi.</MDTypography>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
                      Ajouter un coût
                    </MDTypography>
                    <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
                      <TextField label="Libellé" size="small" value={nouveauCout.libelle}
                        onChange={(e) => setNouveauCout({ ...nouveauCout, libelle: e.target.value })} />
                      <TextField label="Montant €" type="number" size="small" sx={{ width: 110 }} value={nouveauCout.montant}
                        onChange={(e) => setNouveauCout({ ...nouveauCout, montant: e.target.value })} />
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel id="type-cout">Type</InputLabel>
                        <Select labelId="type-cout" label="Type" sx={{ height: 44 }} value={nouveauCout.type}
                          onChange={(e) => setNouveauCout({ ...nouveauCout, type: e.target.value })}>
                          <MenuItem value="par_personne">Par personne</MenuItem>
                          <MenuItem value="fixe_groupe">Fixe groupe</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel id="cat-cout">Catégorie</InputLabel>
                        <Select labelId="cat-cout" label="Catégorie" sx={{ height: 44 }}
                          value={nouveauCout.categorie}
                          onChange={(e) => changerCategorie(e.target.value)}>
                          {CATEGORIES.map((c) => (
                            <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {nouveauCout.categorie === "hotel" && (
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                          <InputLabel id="type-chambre-cout">Chambre concernée</InputLabel>
                          <Select labelId="type-chambre-cout" label="Chambre concernée" sx={{ height: 44 }}
                            value={nouveauCout.type_chambre}
                            onChange={(e) => setNouveauCout({ ...nouveauCout, type_chambre: e.target.value })}>
                            <MenuItem value="">Toutes chambres</MenuItem>
                            {TYPES_CHAMBRE.map((t) => (
                              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                      <MDButton size="small" color="info" onClick={ajouterCout} disabled={!nouveauCout.libelle}>
                        <Icon>add</Icon>
                      </MDButton>
                    </MDBox>
                    <MDTypography variant="caption" color="text" display="block" mt={1}>
                      Le choix de la chambre concernée n&apos;est possible que pour la catégorie Hôtel (les autres coûts sont identiques pour tous les participants).
                    </MDTypography>
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Card>
              <MDBox mx={2} mt={-3} py={2} px={2} variant="gradient" bgColor="success" borderRadius="lg" coloredShadow="success">
                <MDTypography variant="h6" color="white">Résultat de la simulation</MDTypography>
              </MDBox>
              <MDBox p={3}>
                {!sessionId ? (
                  <MDTypography variant="body2" color="text">Sélectionnez une session.</MDTypography>
                ) : (
                  <>
                    <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
                      Participants prévus par type de chambre
                    </MDTypography>
                    <MDBox display="flex" gap={2} mb={2} flexWrap="wrap">
                      {TYPES_CHAMBRE.map((t) => (
                        <TextField key={t.value} label={t.label} type="number" size="small" sx={{ width: 110 }}
                          value={nbParTypeChambre[t.value]}
                          onChange={(e) => setNbParTypeChambre({ ...nbParTypeChambre, [t.value]: e.target.value })} />
                      ))}
                    </MDBox>

                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel id="mode-benef">Logique de bénéfice</InputLabel>
                      <Select labelId="mode-benef" label="Logique de bénéfice" sx={{ height: 44 }}
                        value={modeBenef} onChange={(e) => setModeBenef(e.target.value)}>
                        <MenuItem value="marge_pct">Marge en %</MenuItem>
                        <MenuItem value="benef_pers">Bénéfice fixe par personne (€)</MenuItem>
                        <MenuItem value="benef_total">Bénéfice total visé par type (€)</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      label={modeBenef === "marge_pct" ? "Marge (%)" : modeBenef === "benef_pers" ? "Bénéfice / personne (€)" : "Bénéfice total visé par type (€)"}
                      type="number" fullWidth size="small" sx={{ mb: 2 }}
                      value={valeurBenef} onChange={(e) => setValeurBenef(e.target.value)} />

                    <Divider sx={{ my: 2 }} />

                    {TYPES_CHAMBRE.map((t) => {
                      const r = resultats[t.value];
                      if (r.n === 0 && r.coutsSpecifiques.length === 0) return null;
                      return (
                        <MDBox key={t.value} mb={2} p={2} borderRadius="md" sx={{ bgcolor: "#f5f7fa", border: "1px solid #e0e0e0" }}>
                          <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
                            Chambre {t.label} — {r.n} participant(s)
                          </MDTypography>
                          <MDBox display="flex" justifyContent="space-between" py={0.25}>
                            <MDTypography variant="caption" color="text">Prix de revient / personne</MDTypography>
                            <MDTypography variant="caption" fontWeight="bold" color="error">{fmt(r.revientPers)} €</MDTypography>
                          </MDBox>
                          <MDBox display="flex" justifyContent="space-between" py={0.25}>
                            <MDTypography variant="caption" fontWeight="bold">Prix de vente conseillé</MDTypography>
                            <MDTypography variant="caption" fontWeight="bold" color="success">{fmt(r.prixVente)} €</MDTypography>
                          </MDBox>
                          <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                            Bénéfice : {fmt(r.benefPers)} €/pers · {fmt(r.benefTotal)} € au total
                          </MDTypography>
                        </MDBox>
                      );
                    })}

                    <Divider sx={{ my: 2 }} />

                    <MDBox display="flex" justifyContent="space-between" py={0.5}>
                      <MDTypography variant="button" color="text">Coût total session (tous types + fixes)</MDTypography>
                      <MDTypography variant="button">{fmt(coutTotalSession)} €</MDTypography>
                    </MDBox>
                    <MDBox display="flex" justifyContent="space-between" py={0.5}>
                      <MDTypography variant="button" color="text">Recette totale estimée</MDTypography>
                      <MDTypography variant="button">{fmt(recetteTotalSession)} €</MDTypography>
                    </MDBox>
                    <MDBox display="flex" justifyContent="space-between" py={0.5}>
                      <MDTypography variant="button" fontWeight="bold">Résultat estimé</MDTypography>
                      <MDTypography variant="button" fontWeight="bold" color={recetteTotalSession - coutTotalSession >= 0 ? "success" : "error"}>
                        {fmt(recetteTotalSession - coutTotalSession)} €
                      </MDTypography>
                    </MDBox>
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default OumrahSimulateur;
